import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

function ConversationList({
  conversations,
  currentConversationId,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation,
  onToggleFavorite,
  onArchiveConversation,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const handleMenuOpen = (event, conversation) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleRename = () => {
    setNewName(selectedConversation.name);
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameSubmit = () => {
    if (newName.trim() && selectedConversation) {
      onRenameConversation(selectedConversation.id, newName.trim());
    }
    setRenameDialogOpen(false);
    setNewName('');
  };

  const handleDelete = () => {
    if (selectedConversation) {
      onDeleteConversation(selectedConversation.id);
    }
    handleMenuClose();
  };

  const handleToggleFavorite = () => {
    if (selectedConversation) {
      onToggleFavorite(selectedConversation.id);
    }
    handleMenuClose();
  };

  const handleToggleArchive = () => {
    if (selectedConversation) {
      onArchiveConversation(selectedConversation.id);
    }
    handleMenuClose();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Now';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  // Sort conversations by updatedAt (most recent first)
  const sortedConversations = [...conversations].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

  // Separate active and archived conversations
  const activeConversations = sortedConversations.filter((c) => !c.archived);
  const archivedConversations = sortedConversations.filter((c) => c.archived);

  if (conversations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <ChatIcon sx={{
          fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5,
        }}
        />
        <Typography variant="body2" color="text.secondary">
          No conversations yet. Start chatting to see your conversation history here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}
    >
      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {/* Active Conversations */}
        {activeConversations.map((conversation) => (
          <ListItem key={conversation.id} disablePadding>
            <ListItemButton
              selected={conversation.id === currentConversationId}
              onClick={() => onSelectConversation(conversation.id)}
              sx={{
                py: 1.5,
                px: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
              }}
            >
              <ListItemText
                primary={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      {conversation.name || 'Untitled Conversation'}
                    </Typography>
                    {conversation.favorite && (
                      <FavoriteIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    )}
                  </Box>
                )}
                secondary={(
                  <Box
                    component="span"
                    sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5,
                    }}
                  >
                    <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
                      {conversation.messages?.length || 0}
                      {' '}
                      messages
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ opacity: 0.7 }}>
                      {formatTime(conversation.updatedAt || conversation.createdAt)}
                    </Typography>
                  </Box>
                )}
              />
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, conversation)}
                sx={{
                  ml: 1,
                  color: 'inherit',
                  opacity: 0.7,
                  '&:hover': { opacity: 1 },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          </ListItem>
        ))}

        {/* Archived Conversations */}
        {archivedConversations.length > 0 && (
          <>
            <Box sx={{
              px: 2, py: 1, borderTop: 1, borderColor: 'divider', mt: 1,
            }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Archived
              </Typography>
            </Box>
            {archivedConversations.map((conversation) => (
              <ListItem key={conversation.id} disablePadding>
                <ListItemButton
                  selected={conversation.id === currentConversationId}
                  onClick={() => onSelectConversation(conversation.id)}
                  sx={{
                    py: 1,
                    px: 2,
                    opacity: 0.7,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      opacity: 1,
                    },
                  }}
                >
                  <ListItemText
                    primary={(
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {conversation.name || 'Untitled Conversation'}
                      </Typography>
                    )}
                    secondary={(
                      <Typography component="div" variant="caption">
                        {formatTime(conversation.updatedAt || conversation.createdAt)}
                      </Typography>
                    )}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, conversation)}
                    sx={{ ml: 1, color: 'inherit', opacity: 0.7 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}
      </List>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleRename}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleToggleFavorite}>
          {selectedConversation?.favorite ? (
            <FavoriteBorderIcon sx={{ mr: 1, fontSize: 18 }} />
          ) : (
            <FavoriteIcon sx={{ mr: 1, fontSize: 18 }} />
          )}
          {selectedConversation?.favorite ? 'Remove from Favorites' : 'Add to Favorites'}
        </MenuItem>
        <MenuItem onClick={handleToggleArchive}>
          {selectedConversation?.archived ? (
            <UnarchiveIcon sx={{ mr: 1, fontSize: 18 }} />
          ) : (
            <ArchiveIcon sx={{ mr: 1, fontSize: 18 }} />
          )}
          {selectedConversation?.archived ? 'Unarchive' : 'Archive'}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter conversation name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleRenameSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ConversationList;
